export const DEFAULT_PAGE_INDEX = 0;
export const DEFAULT_PAGE_SIZES = [20, 50, 100, 200];
export const [DEFAULT_PAGE_SIZE] = DEFAULT_PAGE_SIZES;

export const NoopValue = 'NooP';

export type DxColDataType = 'string' | 'datetime' | 'boolean' | 'number';

export const HeaderFilterDefaultMapper = (t: string): DxHeaderFilterItem => ({
	text: t === NoopValue ? $localize`:i18n=@@general.valueNotFound:` : t,
	value: t,
});

export const DcDxGrids = {
	ChartListStandalone: 'dx.grid.charts',
	ConnectorListStandalone: 'dx.grid.connectors',
	DashboardListStandalone: 'dx.grid.dashboards',
	EntityListStandalone: 'dx.grid.entities',
	DatablockListStandalone: 'dx.grid.datablocks',
	ExpositionListStandalone: 'dx.grid.expositions',
	ExpositionListDatablockEmbedded: 'dx.grid.db-expositions',
	GridListStandalone: 'dx.grid.grids',
	MapListStandalone: 'dx.grid.maps',
	MediaListStandalone: 'dx.grid.medias',
	RankingListStandalone: 'dx.grid.rankings',
	TimelineListStandalone: 'dx.grid.timelines',
	TaskListStandalone: 'dx.grid.tasks',
	DepotListStandalone: 'dx.grid.depots',
	OrphanProjectListStandalone: 'dx.grid.orphan-projects',
	SubscriptionListStandalone: 'dx.grid.subscriptions',
	subscriptionListDatablockEmbedded: 'dx.grid.db-subscriptions',
	ProjectListStandalone: 'dx.grid.projects',
	ProjectTraceListStandalone: 'dx.grid.project-traces',
	GroupingTagListStandalone: 'dx.grid.grouping-tag',
};

export enum DxOperations {
	Equal = '=',
	In = 'anyof',
	NotIn = 'noneof',
	NotEmpty = '<>',
	NotEqual = '<>',
	LessThan = '<',
	GreaterThan = '>',
	LessThanOrEqual = '<=',
	GreaterThanOrEqual = '>=',
	Between = 'between',
	Contains = 'contains',
	NotContains = 'notcontains',
	StartsWith = 'startswith',
	EndsWith = 'endswith',
	And = 'and',
	Or = 'or',
}

export const StringFilterOperations = [
	DxOperations.Contains,
	DxOperations.NotContains,
	DxOperations.Equal,
	DxOperations.NotEqual,
];

export const DateFilterOperations = [
	DxOperations.Equal,
	DxOperations.LessThan,
	DxOperations.LessThanOrEqual,
	DxOperations.GreaterThan,
	DxOperations.GreaterThanOrEqual,
	DxOperations.Between,
];

export enum DxFilterElem {
	Header = 'headerFilter',
	FilterRow = 'filterRow',
	FilterBuilder = 'filterBuilder',
}

export type DxSimpleHeaderFilter = { text: string; value: unknown };

export type DxHeaderFilterItem = { text: string; value: string };

/*
export type DxHeaderFilterItem<T = Record<string, unknown>> = {
	text: string;
	value: SimpleFilterExp<T>;
};*/

export type DxSortField = {
	selector: string;
	order: 'asc' | 'desc';
};

export const checkStringEquality = (
	columnValue: string,
	userFilterValue: string,
	op: string
): boolean => {
	const colCI = columnValue ? columnValue.toLowerCase() : '';
	const userCI = userFilterValue ? userFilterValue.toLowerCase() : '';
	if (op === DxOperations.Equal) {
		if (userCI === '') {
			return colCI.length === 0;
		}
		return colCI === userCI;
	}
	if (op === DxOperations.Contains) {
		return colCI.includes(userCI);
	}
	if (op === DxOperations.NotContains) {
		return !colCI.includes(userCI);
	}
	if (op === DxOperations.StartsWith) {
		return colCI.startsWith(userCI);
	}
	if (op === DxOperations.EndsWith) {
		return colCI.endsWith(userCI);
	}
	if (op === DxOperations.NotEmpty || op === DxOperations.NotEqual) {
		if (userCI === '') {
			return colCI.length !== 0;
		}
		return colCI.localeCompare(userCI) !== 0;
	}
	return false;
};

type ExtractFrom<T, U extends T> = U;

type FilterTuple<
	Length extends number,
	Type,
	Acc extends Array<Type> = []
> = Length extends Acc['length']
	? Readonly<Acc>
	: FilterTuple<Length, Type, [...Acc, Type]>;

type FilterValue = string | number | boolean | Date;

type OperatorType = `${DxOperations}`;

type ArrayBasedOperators = ExtractFrom<OperatorType, 'between' | 'anyof'>;

type FilterValueForOperator<Op extends OperatorType> =
	Op extends ArrayBasedOperators
		? Op extends 'between'
			? FilterTuple<2, FilterValue>
			: Array<FilterValue>
		: FilterValue;

export type SimpleFilterExp<RowAttr> = {
	[Op in OperatorType]: [keyof RowAttr, Op, FilterValueForOperator<Op>];
}[OperatorType];

export type CompositeFilterExpr<RowAttr> = {
	[Op in OperatorType]: [
		ColumnCompositeFilterExpr<RowAttr>,
		Op,
		ColumnCompositeFilterExpr<RowAttr>
	];
}[DxOperations.And | DxOperations.Or];

export type ColumnCompositeFilterExpr<RowAttr> = {
	[Op in OperatorType]: [
		SimpleFilterExp<RowAttr>,
		Op,
		SimpleFilterExp<RowAttr>
	];
}[DxOperations.And | DxOperations.Or];

/**
 * Type guard to check if an expression is a SimpleFilterExp.
 */
export const isSimpleFilter = <RowAttr>(
	expr: any
): expr is SimpleFilterExp<RowAttr> =>
	// A simple filter is a 3-element array where the first element is NOT an array.
	Array.isArray(expr) && expr.length === 3 && !Array.isArray(expr[0]);

/**
 * Type guard to check if an expression is a CompositeFilterExpr.
 */
export const isCompositeFilter = <RowAttr>(
	expr: any
): expr is CompositeFilterExpr<RowAttr> =>
	// A composite filter is a n*3-element array where sub element IS an array and sub-sub-element is also an array.
	Array.isArray(expr) &&
	expr.length >= 3 &&
	Array.isArray(expr[0]) &&
	Array.isArray(expr[2]) &&
	(Array.isArray(expr[0][0]) || Array.isArray(expr[0][0]));

/**
 * Type guard to check if an expression is a CompositeFilterExpr.
 */
export const isColumnCompositeFilter = <RowAttr>(
	expr: any
): expr is ColumnCompositeFilterExpr<RowAttr> =>
	// A composite filter is a n*3-element array where sub element IS an array and sub-sub-element is also an array.
	Array.isArray(expr) &&
	expr.length >= 3 &&
	Array.isArray(expr[0]) &&
	Array.isArray(expr[2]) &&
	!Array.isArray(expr[0][0]) &&
	!Array.isArray(expr[0][0]);

export type ParsedSimpleFilter<RowAttr> = {
	type: 'simple';
	key: keyof RowAttr;
	op: OperatorType;
	value: any; // The value from the original filter
};

export type ParsedCompositeFilter<RowAttr> = {
	type: 'composite';
	op: OperatorType;
	left: ParsedFilter<RowAttr>; // Recursively parsed
	right: ParsedFilter<RowAttr>; // Recursively parsed
};

export type ParsedFilter<RowAttr> =
	| ParsedSimpleFilter<RowAttr>
	| ParsedCompositeFilter<RowAttr>;

export type DxFilterField<RowAttr = Record<string, unknown>> = {
	[Op in OperatorType]: {
		field: keyof RowAttr;
		operator: Op;
		value: FilterValueForOperator<Op>;
	};
}[OperatorType];
